import { useState } from 'react';
import { Button, Space, Table, Popconfirm, Modal, Form, Input } from 'antd';
import { faSign, faEdit, faTrash, faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomTitle from '../../components/CustomTitle';
import ButtonIcon from '../../components/ButtonComp';
import CustomLayout from 'components/Layout';

type Plan = {
    id: number;
    name: string;
    description: string;
    created_at: string;
    status: string;
};

const SignalPlanManagement = () => {
    const [plans, setPlans] = useState<Plan[]>([
        { id: 1, name: 'Plan A', description: 'Señalización de emergencia en zona A', created_at: '2025-01-25', status: 'Activo' },
        { id: 2, name: 'Plan B', description: 'Señalización para evacuación en planta B', created_at: '2025-01-26', status: 'Inactivo' },
        { id: 3, name: 'Plan C', description: 'Señalización de tráfico en entrada principal', created_at: '2025-01-27', status: 'Activo' },
    ]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
    const [form] = Form.useForm();

    // Función para eliminar un plan
    const handleDelete = (id: number) => {
        setPlans(plans.filter((plan) => plan.id !== id));
    };

    // Función para abrir el modal de edición
    const handleEdit = (plan: Plan) => {
        setEditingPlan(plan);
        form.setFieldsValue(plan); // Establece los valores en el formulario del modal
        setIsModalVisible(true);
    };

    // Función para cerrar el modal
    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingPlan(null);
    };

    // Función para guardar los cambios de un plan
    const handleSave = (values: any) => {
        if (editingPlan) {
            const updatedPlans = plans.map((plan) =>
                plan.id === editingPlan.id ? { ...plan, ...values } : plan
            );
            setPlans(updatedPlans);
            setIsModalVisible(false);
            setEditingPlan(null);
        }
    };

    // Configuración de las columnas de la tabla
    const columns = [
        {
            title: '#',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Nombre del plan',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Descripción',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Fecha de creación',
            dataIndex: 'created_at',
            key: 'created_at',
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
        },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_:any, record: Plan) => (
                <Space size="middle">
                    <Button
                        icon={<FontAwesomeIcon icon={faEdit} />}
                        onClick={() => handleEdit(record)}
                        type="primary"
                        size="small"
                    />
                    <Popconfirm
                        title="¿Seguro que deseas eliminar este plan?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Sí"
                        cancelText="No"
                    >
                        <Button
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            style={{ border: '1px solid red', color: 'red' }}
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <CustomLayout>
            <CustomTitle
                title='Planes de Señalización'
                actions={[
                    <ButtonIcon icon={faFilter} tooltip='Filtrar resultados' variant='outline-primary'/>
                ]}
            />
            <Button
                icon={<FontAwesomeIcon icon={faSign} />}
                type="primary"
                size="large"
                onClick={() => handleEdit({ id: 0, name: '', description: '', created_at: '', status: '' })}
                style={{ marginBottom: 20 }}
            >
                Crear nuevo plan
            </Button>
            <Table
                columns={columns}
                dataSource={plans}
                rowKey="id"
                pagination={{ pageSize: 5 }}
            />

            {/* Modal de edición de plan */}
            <Modal
                title={editingPlan ? 'Editar Plan de Señalización' : 'Crear Plan de Señalización'}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                <Form
                    form={form}
                    onFinish={handleSave}
                    initialValues={editingPlan || {}}
                >
                    <Form.Item
                        name="name"
                        label="Nombre del plan"
                        rules={[{ required: true, message: 'Por favor ingrese el nombre del plan' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Descripción"
                        rules={[{ required: true, message: 'Por favor ingrese la descripción' }]}
                    >
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item
                        name="status"
                        label="Estado"
                        rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="created_at"
                        label="Fecha de creación"
                        rules={[{ required: true, message: 'Por favor ingrese la fecha de creación' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {editingPlan ? 'Guardar cambios' : 'Crear plan'}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </CustomLayout>
    );
};

export default SignalPlanManagement;
